/**
 * @fileoverview Base renderer with common functionality
 *
 * All table renderers inherit from this base class which provides:
 * - Common styling application
 * - Event handler tracking
 * - Loading/error state management
 * - Cleanup utilities
 *
 * @module renderers/BaseRenderer
 */

import { CSS_CLASSES, ICONS, DEFAULTS } from '../constants.js';

/**
 * @typedef {import('../types.js').TableOptions} TableOptions
 * @typedef {import('../types.js').RenderContext} RenderContext
 * @typedef {import('../types.js').EventHandler} EventHandler
 */

/**
 * BaseRenderer - Abstract base class for all renderers
 * @class
 */
export class BaseRenderer {
  /**
   * Create a BaseRenderer instance
   * @param {RenderContext} context - Render context
   */
  constructor(context) {
    this.options = context.options;
    this.container = context.container;
    this.data = context.data;

    // State
    this.tableElement = null;
    this.eventHandlers = [];
    this.isRendered = false;

    // Metrics
    this.metrics = {
      renderStartTime: 0,
      renderTime: 0
    };
  }

  /**
   * Render the table (to be implemented by subclasses)
   * @abstract
   * @returns {Promise<void>}
   */
  async render() {
    throw new Error('BaseRenderer.render() must be implemented by subclass');
  }

  /**
   * Destroy renderer and cleanup
   */
  destroy() {
    this.cleanupEventHandlers();
    this.isRendered = false;
  }

  /**
   * Apply modern styling to table
   * @protected
   */
  applyModernStyling() {
    if (!this.options.useModernStyling) {
      return;
    }

    const compactClass = this.options.compactMode ? ` ${CSS_CLASSES.TABLE_COMPACT}` : '';

    // Check if already styled
    if (this.container.classList.contains(CSS_CLASSES.TABLE_WRAPPER)) {
      if (this.options.compactMode && !this.container.classList.contains(CSS_CLASSES.TABLE_COMPACT)) {
        this.container.classList.add(CSS_CLASSES.TABLE_COMPACT);
      }
      return;
    }

    // If container is the table itself, wrap it
    if (this.container.tagName === 'TABLE') {
      // Only wrap if table is in the DOM (has a parent)
      if (this.container.parentNode) {
        const wrapper = document.createElement('div');
        wrapper.className = CSS_CLASSES.TABLE_WRAPPER + compactClass;
        this.container.parentNode.insertBefore(wrapper, this.container);
        wrapper.appendChild(this.container);
      }
    } else {
      // Container is a div, add wrapper class
      this.container.classList.add(CSS_CLASSES.TABLE_WRAPPER);
      if (compactClass) {
        this.container.classList.add(CSS_CLASSES.TABLE_COMPACT);
      }
    }
  }

  /**
   * Wrap the rendered table in a horizontal-scroll div so wide tables can
   * scroll without scrolling the search/length/info/pagination controls.
   * Must be called AFTER DataTables init since DataTables wraps the table
   * in .dataTables_wrapper and we want our scroll div inside that.
   * @protected
   */
  wrapTableForScroll() {
    if (!this.options.useModernStyling || !this.tableElement) return;
    const $table = jQuery(this.tableElement);
    if ($table.parent('.cxgn-table-scroll').length) return;
    $table.wrap('<div class="cxgn-table-scroll"></div>');
  }

  /**
   * Show loading indicator
   * @protected
   */
  showLoading() {
    let loadingHtml;

    if (this.options.useModernStyling) {
      loadingHtml = `
        <div class="${CSS_CLASSES.LOADING}">
          <div class="spinner"></div>
          <p>Loading table data...</p>
        </div>
      `;
    } else {
      loadingHtml = `
        <div class="${CSS_CLASSES.LOADING_LEGACY}" style="padding: 50px;">
          <i class="${ICONS.REFRESH_SPIN}" style="font-size: 48px; color: #5cb85c;"></i>
          <p style="margin-top: 20px; font-size: 16px; color: #666;">Loading table data...</p>
        </div>
      `;
    }

    this.container.innerHTML = loadingHtml;
  }

  /**
   * Hide loading indicator
   * @protected
   */
  hideLoading() {
    const loadingElement = this.container.querySelector(`.${CSS_CLASSES.LOADING}`);
    if (loadingElement) {
      loadingElement.remove();
    }
  }

  /**
   * Show error message
   * @protected
   * @param {string} message - Error message
   * @param {function} [retryCallback] - Optional retry callback
   */
  showError(message, retryCallback) {
    const errorContainer = document.createElement('div');
    errorContainer.className = CSS_CLASSES.ERROR;

    if (this.options.useModernStyling) {
      errorContainer.classList.add(CSS_CLASSES.TABLE_WRAPPER);
    }

    const alertDiv = document.createElement('div');
    alertDiv.className = CSS_CLASSES.ALERT_DANGER;
    alertDiv.setAttribute('role', 'alert');
    alertDiv.style.position = 'relative';

    // Icon and message
    const icon = document.createElement('i');
    icon.className = ICONS.EXCLAMATION;
    icon.style.marginRight = '8px';
    alertDiv.appendChild(icon);

    const strong = document.createElement('strong');
    strong.textContent = 'Unable to load table data';
    alertDiv.appendChild(strong);
    alertDiv.appendChild(document.createElement('br'));

    const messageSpan = document.createElement('span');
    messageSpan.textContent = message || 'An unexpected error occurred';
    alertDiv.appendChild(messageSpan);

    // Retry button
    if (retryCallback) {
      alertDiv.appendChild(document.createElement('br'));
      alertDiv.appendChild(document.createElement('br'));

      const retryBtn = document.createElement('button');
      retryBtn.className = 'btn btn-sm btn-default';
      retryBtn.style.marginLeft = '10px';
      retryBtn.innerHTML = `<i class="${ICONS.REFRESH}"></i> Retry`;

      const retryHandler = () => retryCallback();
      retryBtn.addEventListener('click', retryHandler);
      this.trackEventHandler(retryBtn, 'click', retryHandler);

      alertDiv.appendChild(retryBtn);
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'close';
    closeBtn.setAttribute('data-dismiss', 'alert');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    closeBtn.style.position = 'absolute';
    closeBtn.style.top = '10px';
    closeBtn.style.right = '10px';

    const closeHandler = () => errorContainer.style.display = 'none';
    closeBtn.addEventListener('click', closeHandler);
    this.trackEventHandler(closeBtn, 'click', closeHandler);

    alertDiv.appendChild(closeBtn);
    errorContainer.appendChild(alertDiv);

    this.container.innerHTML = '';
    this.container.appendChild(errorContainer);

    // Log to console
    console.error('BaseRenderer Error:', message);

    // Call error callback if provided
    if (this.options.onError) {
      this.options.onError(message);
    }
  }

  /**
   * Create table element with basic structure
   * @protected
   * @returns {HTMLTableElement}
   */
  createTableElement() {
    const table = document.createElement('table');
    table.className = CSS_CLASSES.TABLE_BASE;

    const thead = document.createElement('thead');
    const tbody = document.createElement('tbody');

    table.appendChild(thead);
    table.appendChild(tbody);

    return table;
  }

  /**
   * Create table header from columns
   * @protected
   * @param {Array} columns - Column definitions
   * @returns {HTMLTableSectionElement}
   */
  createTableHeader(columns) {
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    columns.forEach(col => {
      const th = document.createElement('th');
      th.textContent = col.title || '';

      if (col.width) {
        th.style.width = col.width;
      }

      if (col.className) {
        th.className = col.className;
      }

      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    return thead;
  }

  /**
   * Track event handler for cleanup
   * @protected
   * @param {HTMLElement} element - Element with event
   * @param {string} event - Event name
   * @param {function} handler - Event handler
   */
  trackEventHandler(element, event, handler) {
    this.eventHandlers.push({ element, event, handler });
  }

  /**
   * Clean up all tracked event handlers
   * @protected
   */
  cleanupEventHandlers() {
    this.eventHandlers.forEach(({ element, event, handler }) => {
      try {
        element.removeEventListener(event, handler);
      } catch (error) {
        console.warn('BaseRenderer: Error removing event handler:', error);
      }
    });
    this.eventHandlers = [];
  }

  /**
   * Start render timing
   * @protected
   */
  startRenderTiming() {
    this.metrics.renderStartTime = performance.now();
  }

  /**
   * End render timing
   * @protected
   */
  endRenderTiming() {
    this.metrics.renderTime = performance.now() - this.metrics.renderStartTime;
    console.log(`${this.constructor.name}: Rendered in ${Math.round(this.metrics.renderTime)}ms`);
  }

  /**
   * Get render metrics
   * @returns {Object}
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Ensure table element exists
   * @protected
   * @returns {HTMLTableElement}
   */
  ensureTableElement() {
    if (!this.tableElement) {
      // Check if container is a table
      if (this.container.tagName === 'TABLE') {
        this.tableElement = this.container;
      } else {
        // Check for existing table in container
        const existingTable = this.container.querySelector('table');
        if (existingTable) {
          this.tableElement = existingTable;
        } else {
          // Create new table
          this.tableElement = this.createTableElement();
          this.container.appendChild(this.tableElement);
        }
      }
    }
    return this.tableElement;
  }

  /**
   * Get cell value from row data
   * @protected
   * @param {Object|Array} rowData - Row data
   * @param {Object} column - Column definition
   * @param {number} rowIndex - Row index
   * @returns {*} Cell value
   */
  getCellValue(rowData, column, rowIndex) {
    let value = '';

    if (column.data) {
      if (typeof column.data === 'function') {
        value = column.data(rowData, 'display', rowData, rowIndex);
      } else if (typeof column.data === 'number') {
        value = Array.isArray(rowData) ? rowData[column.data] : rowData[column.data];
      } else {
        value = rowData[column.data];
      }
    }

    // Handle null/undefined with defaultContent
    if ((value == null || value === '') && column.defaultContent !== undefined) {
      value = column.defaultContent;
    }

    // Apply render function if present
    if (column.render && typeof column.render === 'function') {
      value = column.render(value, 'display', rowData, rowIndex);
    }

    return value;
  }

  /**
   * Create a table cell
   * @protected
   * @param {*} value - Cell value
   * @param {Object} column - Column definition
   * @returns {HTMLTableCellElement}
   */
  createTableCell(value, column) {
    const td = document.createElement('td');

    if (column.className) {
      td.className = column.className;
    }

    // Set cell content (handle HTML)
    if (typeof value === 'string' && (column.render || value.indexOf('<') !== -1)) {
      td.innerHTML = value;
    } else {
      td.textContent = value != null ? String(value) : '';
    }

    return td;
  }

  /**
   * Create a table row
   * @protected
   * @param {Object|Array} rowData - Row data
   * @param {number} rowIndex - Row index
   * @param {Array} columns - Column definitions
   * @returns {HTMLTableRowElement}
   */
  createTableRow(rowData, rowIndex, columns) {
    const tr = document.createElement('tr');

    columns.forEach(column => {
      const value = this.getCellValue(rowData, column, rowIndex);
      const td = this.createTableCell(value, column);
      tr.appendChild(td);
    });

    // Add row click handler
    if (this.options.onRowClick) {
      const clickHandler = () => {
        this.options.onRowClick(rowData, tr, rowIndex);
      };
      tr.addEventListener('click', clickHandler);
      this.trackEventHandler(tr, 'click', clickHandler);
    }

    return tr;
  }

  /**
   * Check if DataTables library is available
   * @protected
   * @returns {boolean}
   */
  isDataTablesAvailable() {
    return typeof jQuery !== 'undefined' && typeof jQuery.fn.DataTable !== 'undefined';
  }

  /**
   * Strip HTML tags from string
   * @protected
   * @param {string} html - HTML string
   * @returns {string} Plain text
   */
  stripHTML(html) {
    if (typeof html !== 'string') return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}

export default BaseRenderer;
